import React from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Plus } from 'lucide-react';

const faqs = [
  {
    question: "Do I need previous experience to work in adult entertainment?",
    answer: "Not always. Some Melbourne venues specifically advertise opportunities for people without any previous experience, whilst others may prefer applicants who already understand the industry. Even if you're completely new, don't rule yourself out before asking questions to a potential employer. Skills from other jobs—customer service, hospitality, communication, confidence, organisation—are all valuable in this industry."
  },
  {
    question: "What types of roles are available?",
    answer: "Opportunities may include dancers, entertainers, performers, hosts, promotional staff, receptionists, waitresses, bar staff and other support positions. You don't necessarily need to be a performer to work within the sector. There are various front-of-house and entertainment positions available across different parts of Melbourne."
  },
  {
    question: "Is the work flexible?",
    answer: "Yes. Employers across Melbourne offer casual, part-time and full-time roles, and many venues advertise adaptable schedules. As a contractor, you can choose a shift schedule that suits your lifestyle, whether you're a student, a parent, or have another job. Before accepting a role, ask about expected hours, available shifts and minimum commitments."
  },
  {
    question: "How does payment work?",
    answer: "Most brothels operate a contractor model where clients pay a fee for services, which is split between you and the venue. In most cases, you'll retain a significant majority share of the standard appointment fee. The percentage the parlour receives varies, but the fee goes towards staff wages and facility maintenance. Ask about the specific split before starting."
  },
  {
    question: "What should I ask before accepting a job?",
    answer: "Consider asking about: working hours, payment structure and fee splits, any fees or deductions, specific duties expected, training available, equipment or anything you need to bring, performance expectations, whether there are trials or auditions, and any qualifications needed. A reputable employer should explain the role clearly."
  },
  {
    question: "Will my boundaries be respected?",
    answer: "Your comfort and boundaries should always be at the forefront. You always have the absolute right to refuse a client—venues cannot force you to take on someone you don't want to receive. Make sure you understand exactly what a role involves, and you should feel able to decline anything outside the agreed responsibilities."
  },
  {
    question: "Is sex work legal in Melbourne?",
    answer: "Yes. Victoria completely decriminalised sex work and transitioned away from the old licensing system. The industry now operates under mainstream economic regulation, meaning you're free to work in the sector just as you might in hospitality or other industries. You're also protected under Victoria's Equal Opportunity Act."
  },
  {
    question: "How do I get started?",
    answer: "Start with a conversation. SMS Lisa at 0429 085683 to introduce yourself, explain the type of work you're interested in, and ask questions. You can find out whether we're recruiting, what roles are available, and what the next steps look like. Speaking honestly and openly helps us understand what you can bring to the role."
  }
];

const FAQSection = () => {
  return (
    <section 
      id="faq" 
      className="relative py-24 lg:py-32 bg-[#121216]"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="font-mono text-xs text-[#E02852] tracking-[0.3em] uppercase block mb-4">
            FAQ
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F7F6F2] tracking-tight mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Everything you need to know before you start
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <AccordionItem 
                value={`item-${index}`}
                className="border border-[rgba(255,255,255,0.06)] rounded-xl overflow-hidden bg-[#18181F] data-[state=open]:border-[rgba(224,40,82,0.3)]"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger 
                  className="faq-trigger px-6 py-5 text-left hover:no-underline group"
                >
                  <span className="flex items-center gap-4 text-[#F7F6F2] font-medium text-base sm:text-lg pr-4">
                    <span className="font-mono text-xs text-[#E02852] tracking-wider">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="faq-content px-6 pb-6 pt-0">
                  <div className="pl-10 text-[#A1A1AA] leading-relaxed">
                    {faq.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>

        {/* Additional CTA */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-[#A1A1AA] mb-4">
            Have more questions?
          </p>
          <a
            href="sms:0429085683?body=Hi%20Lisa,%20I%20have%20a%20question%20about%20employment%20opportunities."
            className="inline-flex items-center gap-2 text-[#E02852] hover:text-[#F7F6F2] transition-colors font-medium"
            data-testid="faq-sms-link"
          >
            SMS Lisa: 0429 085683
            <span className="text-xs">(SMS only)</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
